import { get } from 'lodash';
import {
  convertServiceType,
  convertFulfillmentOrderDropOffDetail,
  getCashOnDeliveryInfo,
} from 'views/v2/shopify-home/shopify-orders/utils';
import {
  getSelectedPayment,
  getSelectedQuote,
  getPaidBy,
  getItemInfo,
  getRecipientInfo,
  getSelectedServiceType,
} from 'helper/order';
import { getUserCountryCode, isUsingPaxPersonalPayments } from 'helper/user';
import { enterpriseForQuoteParam } from 'helper/quote';
import { FIELD_NAME } from 'views/v2/home/constants';

const LAST_SELECTED_SHOP_LOCALSTORAGE_KEY = '__last_selected_shop__';
const MSD_ORDERS_UPDATE_QUOTE_REQUEST_ID = 'test-msd-orders-update-epic';
const MULTIPLE_ORDERS_UPDATE_QUOTE_REQUEST_ID =
  'test-multiple-orders-update-epic';

export const toGetShopifyMultipleOrdersBatchQuote = (
  orders,
  userInfo,
  payments,
) => {
  const { serviceTypes, shop } = orders[0];
  const serviceTypesArr = serviceTypes.map(convertServiceType);

  const deliveries = orders.map(order => {
    const { fulfillmentOrder, pickUpAddress } = order;
    const payment = getSelectedPayment(order, payments);
    return {
      fulfillmentOrders: [
        convertFulfillmentOrderDropOffDetail(
          fulfillmentOrder,
          fulfillmentOrder.addressEntity,
        ),
      ],
      pickUp: pickUpAddress,
      cityCode: get(pickUpAddress, 'grabPoi.city'),
      countryCode: getUserCountryCode(userInfo),
      paymentMethod: {
        id: payment.paymentTypeID,
        name: payment.refNumber,
        type: payment.type,
      },
      paidBy: getPaidBy(order),
    };
  });

  return {
    deliveries,
    serviceTypes: serviceTypesArr,
    quoteRequestID: MULTIPLE_ORDERS_UPDATE_QUOTE_REQUEST_ID,
    shop,
  };
};

export const toGetShopifyMultipleOrdersQuote = (order, userInfo, payments) => {
  const { fulfillmentOrder, pickUpAddress, serviceTypes, shop } = order;
  const serviceTypesArr = serviceTypes.map(convertServiceType);
  const payment = getSelectedPayment(order, payments);
  const deliveries = [
    {
      fulfillmentOrders: [
        convertFulfillmentOrderDropOffDetail(
          fulfillmentOrder,
          fulfillmentOrder.addressEntity,
        ),
      ],
      pickUp: pickUpAddress,
      cityCode: get(pickUpAddress, 'grabPoi.city'),
      countryCode: getUserCountryCode(userInfo),
      paymentMethod: {
        id: payment.paymentTypeID,
        name: payment.refNumber,
        type: payment.type,
      },
      paidBy: getPaidBy(order),
    },
  ];

  return {
    deliveries,
    serviceTypes: serviceTypesArr,
    quoteRequestID: MULTIPLE_ORDERS_UPDATE_QUOTE_REQUEST_ID,
    shop,
  };
};

export const toGetShopifyMsdOrdersQuote = (order, userInfo, payments) => {
  const { fulfillmentOrders, pickUpAddress, serviceTypes, shop } = order;
  const serviceTypesArr = serviceTypes.map(convertServiceType);
  const payment = getSelectedPayment(order, payments);
  const deliveries = [
    {
      fulfillmentOrders: fulfillmentOrders.map(fulfillmentOrder =>
        convertFulfillmentOrderDropOffDetail(
          fulfillmentOrder,
          fulfillmentOrder.addressEntity,
        ),
      ),
      pickUp: pickUpAddress,
      cityCode: get(pickUpAddress, 'grabPoi.city'),
      countryCode: getUserCountryCode(userInfo),
      paymentMethod: {
        id: payment.paymentTypeID,
        name: payment.refNumber,
        type: payment.type,
      },
      paidBy: getPaidBy(order),
    },
  ];

  return {
    deliveries,
    serviceTypes: serviceTypesArr,
    quoteRequestID: MSD_ORDERS_UPDATE_QUOTE_REQUEST_ID,
    shop,
  };
};

const toCreateShopifyBaseDeliveries = (order, userInfo, payments) => {
  const payment = getSelectedPayment(order, payments);
  const quote = getSelectedQuote(order);
  const serviceType = getSelectedServiceType(order);

  return {
    serviceType: {
      serviceID: serviceType.ID,
      expressServiceID: serviceType.expressServiceID,
      serviceTypeName: serviceType.name,
    },
    pickUp: get(order, 'pickUpAddress'),
    quoteSignature: quote.quoteSignature,
    paymentMethod: {
      id: payment.paymentTypeID,
      name: payment.refNumber,
      type: payment.type,
    },
    paidBy: getPaidBy(order),
    countryCode: getUserCountryCode(userInfo),
    cityCode: get(order, 'pickupAddress.grabPoi.city'),
  };
};

export const toCreateShopifyMultipleDeliveries = (
  orders,
  userInfo,
  payments,
) => {
  const result = {};
  result.shop = get(orders, '[0].shop');
  result.requests = orders.map(order => {
    const recipient = getRecipientInfo(order)[0];
    const itemInfo = getItemInfo(order);
    return {
      ...toCreateShopifyBaseDeliveries(order, userInfo, payments),
      noteToDriver: `[${order.fulfillmentOrder.orderName}] ${get(
        order[FIELD_NAME.INSTRUCTION],
        'value',
        '',
      )}`.trim(),
      fulfillmentOrders: [
        {
          ...order.fulfillmentOrder,
          name: order.fulfillmentOrder.orderName,
          dropOff: {
            contact: {
              name: recipient.name,
              phone: recipient.phone,
            },
            jobType: 1,
            location: {
              address: recipient.address,
              keywords: recipient.keywords,
              coordinates: recipient.coordinates,
              poiID: recipient.poiID,
            },
            cashOnDelivery: getCashOnDeliveryInfo(order.fulfillmentOrder),
            itemInfo,
          },
        },
      ],
    };
  });
  if (!isUsingPaxPersonalPayments()) {
    result.enterprise = enterpriseForQuoteParam(userInfo);
  }
  return { request: result };
};

export const toCreateShopifyMsdDeliveries = (order, userInfo, payments) => {
  const result = {};
  result.shop = get(order, 'shop');
  const recipientMap = getRecipientInfo(order);
  const itemInfo = getItemInfo(order);
  result.requests = [
    {
      ...toCreateShopifyBaseDeliveries(order, userInfo, payments),
      noteToDriver: `${order.fulfillmentOrders.reduce(
        (acc, fulfillmentOrder) => (acc += `[${fulfillmentOrder.orderName}]`),
        '',
      )} ${get(order[FIELD_NAME.INSTRUCTION], 'value', '')}`.trim(),
      fulfillmentOrders: order.fulfillmentOrders.map((fulfillmentOrder, i) => {
        const recipient = recipientMap[i];
        return {
          ...fulfillmentOrder,
          name: fulfillmentOrder.orderName,
          dropOff: {
            contact: {
              name: recipient.name,
              phone: recipient.phone,
            },
            jobType: 1,
            location: {
              address: recipient.address,
              keywords: recipient.keywords,
              coordinates: recipient.coordinates,
              poiID: recipient.poiID,
            },
            cashOnDelivery: getCashOnDeliveryInfo(fulfillmentOrder),
            itemInfo,
          },
        };
      }),
    },
  ];
  if (!isUsingPaxPersonalPayments()) {
    result.enterprise = enterpriseForQuoteParam(userInfo);
  }
  return { request: result };
};

export const getSelectedShopifyShop = shops => {
  if (!shops) return null;
  const lastShop = window.localStorage.getItem(
    LAST_SELECTED_SHOP_LOCALSTORAGE_KEY,
  );
  const defaultShop = lastShop
    ? find(shops, s => s.shop === lastShop) || shops[0]
    : shops[0];

  return defaultShop;
};

export default {};
